import { PlusIcon } from "assets/icons";
import cn from "classnames";
import { DragDropList, DragDropChangeValue, If } from "components";
import { moveElementInArray } from "helpers";
import { useBoolean } from "hooks";
import isEmpty from "lodash/isEmpty";
import { ModeratorRoutesPrivileges, useHasPrivilege } from "modules/common";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Icon } from "ui";

import { useCategoryPriority } from "../../apis";
import { Category } from "../../categories.types";
import { AttributeTypes, CategoryTypes } from "../../constants/enums";
import { useCategoriesNavigation } from "../../hooks";
import { CategoryItem } from "../CategoryItem";
import { DeleteCategory } from "../DeleteCategory";
import { CategoryListProps } from "./CategoryList.types";

import styles from "./CategoryList.module.scss";

const CategoryList = ({
  type,
  title,
  isDisabled = false,
  isViewMode = false,
  useCategories,
  CategoryModal
}: CategoryListProps) => {
  const isSubChildCategoryType = type === CategoryTypes.SUB_CHILD_CATEGORY;

  const {
    value: isOpenCategoryModal,
    setTrue: onOpenCategoryModal,
    setFalse: closeCategoryModal
  } = useBoolean(false);

  const [category, setCategory] = useState<Category | null>(null);

  const navigate = useNavigate();
  const { search } = useLocation();

  // The guide endpoints are gated on Content, so an admin without it would only
  // ever see a 403 — hide the action rather than let them find out that way.
  const canEditUsageGuide = useHasPrivilege(ModeratorRoutesPrivileges.Content);

  const { currentCategoryId, onChangeCategoryId, resetCategoriesIds } =
    useCategoriesNavigation(type);

  const onEditCategory = (categoryToEdit: Category) => {
    return () => {
      setCategory(categoryToEdit);
      onOpenCategoryModal();
    };
  };

  const onEditCategoryUsageGuide = (categoryId: string) => {
    return () =>
      // The tree's selection lives in the query string; carry it so the back
      // button lands on the same three columns rather than a reset tree.
      navigate(`/categories/usage-guide/${categoryId}`, {
        state: { categoriesSearch: search }
      });
  };

  const onSelectCategory = (categoryId: string) => {
    return () => onChangeCategoryId(categoryId);
  };

  const onCloseModal = () => {
    setCategory(null);
    closeCategoryModal();
  };

  const {
    isLoading: isLoadingCategories,
    categories,
    setCategories,
    refetch: refetchCategories
  } = useCategories();

  const { mutate: mutateCategoryPriority } = useCategoryPriority();

  const onChangeCategoryPriority = (value: DragDropChangeValue) => {
    const { draggableId, fromIndex, toIndex } = value;

    setCategories(
      moveElementInArray({
        array: categories,
        fromIndex,
        toIndex
      })
    );

    mutateCategoryPriority({ id: draggableId, moveToIndex: toIndex });
  };

  const onDeleteCategory = () => {
    resetCategoriesIds();
    refetchCategories();
    onCloseModal();
  };

  const renderCategoryItem = (categoryItem: Category) => {
    const { id, name, image, attributes } = categoryItem;

    const isAttributes =
      attributes?.some(
        (attribute) => attribute.type !== AttributeTypes.Required
      ) ?? false;

    return (
      <CategoryItem
        name={name}
        image={image?.url ?? null}
        isSelected={currentCategoryId === id}
        isViewMode={isViewMode}
        isSubChildCategory={isSubChildCategoryType}
        isAttributes={isAttributes}
        canEditUsageGuide={canEditUsageGuide}
        onSelect={onSelectCategory(id)}
        onEdit={onEditCategory(categoryItem)}
        onEditUsageGuide={onEditCategoryUsageGuide(id)}
      />
    );
  };

  return (
    <>
      <CategoryModal
        isOpen={isOpenCategoryModal}
        category={category}
        refetchCategories={refetchCategories}
        onClose={onCloseModal}
        renderDeleteCategory={
          !isEmpty(category) ? (
            <DeleteCategory
              id={category.id}
              type={type}
              name={category.name}
              onDelete={onDeleteCategory}
            />
          ) : null
        }
      />

      <div className={styles["category-list"]}>
        <header className={styles["category-list__header"]}>
          <h4
            className={cn(styles["category-list__header--title"], {
              [styles["category-list__header--title-disabled"]]: isDisabled
            })}
          >
            {title}
          </h4>

          <If condition={!isViewMode}>
            <button
              className={cn(
                "reset-button",
                styles["category-list__header--action"]
              )}
              disabled={isDisabled}
              onClick={onOpenCategoryModal}
            >
              <Icon as={PlusIcon} boxSize={24} color="#fff" />
            </button>
          </If>
        </header>

        <If condition={!isDisabled}>
          <DragDropList<Category>
            isLoading={isLoadingCategories}
            droppableId={type}
            data={categories}
            isDragDisabled={isViewMode}
            keyForReorder={"priority"}
            onChange={onChangeCategoryPriority}
            renderItem={renderCategoryItem}
          />
        </If>
      </div>
    </>
  );
};

export default CategoryList;
